import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";
import { Badge, Button, Modal } from "@/shared/rd";
import {
  requestFcmToken,
  registerFcmTokenWithBackend,
} from "@/lib/firebase/fcm";

const DISMISSED_KEY = "a2z_admin_notification_prompt_dismissed_at";

function isDismissed(): boolean {
  try {
    // Session-only: every fresh app open asks again until enabled.
    return sessionStorage.getItem(DISMISSED_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Fetch + register the FCM token AFTER the banner has already closed. Run in the
 * background because `getToken()` has no timeout and can hang (service worker /
 * push endpoint) — awaiting it in the click handler previously left the Enable
 * button stuck on a loading state with the banner open. Failures are non-fatal:
 * FcmBootstrap re-attempts registration on the next app load.
 */
async function registerFcmInBackground(): Promise<void> {
  try {
    const token = await requestFcmToken();
    if (token) {
      await registerFcmTokenWithBackend(token);
    }
  } catch {
    // Non-fatal — the user already granted permission; retried on next load.
  }
}

export function NotificationPromptBanner() {
  const t = useTranslations();
  const [dismissed, setDismissed] = useState(isDismissed);
  const [requesting, setRequesting] = useState(false);

  if (dismissed) return null;
  if (typeof Notification === "undefined") return null;
  if (Notification.permission === "granted") return null;
  if (Notification.permission === "denied") return null;

  const handleEnable = async () => {
    setRequesting(true);
    let permission: NotificationPermission = "default";
    try {
      permission = await Notification.requestPermission();
    } catch {
      // Some browsers reject rather than returning "denied"; treat as no grant.
    }
    // Close the banner as soon as the user decides — never wait on the FCM
    // token round-trip (getToken can hang and would leave this stuck open).
    setRequesting(false);
    dismiss();
    if (permission === "granted") {
      void registerFcmInBackground();
    }
  };

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch { /* private browsing */ }
    setDismissed(true);
  };

  // A short decision, so a Modal: enable or later.
  return (
    <Modal
      open
      onClose={dismiss}
      title={t(keys.admin.notificationsPromptTitle)}
      width={380}
      actions={
        <>
          <Button variant="ghost" muted onClick={dismiss}>
            {t(keys.admin.notificationsPromptLater)}
          </Button>
          <Button variant="primary" onClick={() => void handleEnable()} disabled={requesting}>
            {t(keys.admin.notificationsPromptEnable)}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <Badge id="bell" size={36} />
        <p className="rd-modal-text" style={{ margin: 0 }}>
          {t(keys.admin.notificationsPromptBody)}
        </p>
      </div>
    </Modal>
  );
}
